import { type Theme } from '@mui/material/styles'

import { parseISO } from 'date-fns/parseISO'

import { getCategoryColor } from '../../../../core/utils/CategoryUtil'
import { buildDynamicCopy } from '../../../../core/utils/Localization'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'
import { DATE_FORMATS_INTL, formatDate } from '../../../../core/constants'
import type { DataSeries, InstanceSlot } from '../../../../core'
import type { InsightsInstanceSlotData } from '../../../../core/types/InsightsInstanceSlotData'
import { getLayoutConfig } from './layout'

export const BAR_CHART_VARIANTS = {
  CATEGORY_COLOR: 'category_color',
  SAVINGS_ACCOUNT_BALANCES_SUMMARY: 'savings_account_balances_summary',
  MONTHLY_SPEND_COMPARISON: 'monthly_spend_comparison',
}

export interface ChartDataSeriesItem {
  date?: string
  amount?: number
  guid?: string
  label?: string
  aggregated_guids?: string[]
  localized_label?: string
  category_guid?: string
}

export const buildDataSeries = (data: DataSeries | undefined): ChartDataSeriesItem[] => {
  if (!data) {
    return []
  }

  if (data.data) {
    return data.data.map((d) => {
      const item: any = {}

      data.names?.map((name, index) => {
        item[name] = d[index]
      })
      return item
    })
  } else {
    return []
  }
}

export const buildChartData = (
  dataSeries: ChartDataSeriesItem[],
  ariaLabel: string,
  dateFormat: Parameters<typeof formatDate>[1] = DATE_FORMATS_INTL.MONTH_SHORT,
) => {
  const data = [...dataSeries]
    .sort((a, b) => {
      if (!a.date || !b.date) return 0

      return new Date(a.date).getTime() - new Date(b.date).getTime()
    })
    .map((total) => {
      const label = total.date ? formatDate(parseISO(total.date), dateFormat) : (total.label ?? '')
      const amount = total.amount ?? 0

      return {
        ariaLabel: buildDynamicCopy(ariaLabel, label, formatCurrency(amount, '0.00')) as string,
        date: total.date ?? null,
        label,
        value: amount,
        formattedAmount: formatCurrency(amount, '0,0'),
      }
    })

  return data
}

export const barChartVariant = (
  instanceSlot: InstanceSlot,
  instanceSlotData: InsightsInstanceSlotData,
  theme: Theme,
) => {
  const { variant } = instanceSlot
  const barChartLayout = getLayoutConfig(theme)
  if (variant === BAR_CHART_VARIANTS.CATEGORY_COLOR && instanceSlotData.category_guid) {
    const categoryColor = getCategoryColor(instanceSlotData.category_guid, theme)
    return {
      color: categoryColor,
    }
  }
  return {
    color:
      barChartLayout[variant as keyof typeof barChartLayout]?.color ?? theme.palette.primary.main,
  }
}
